import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ExternalLink, Filter } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryImages = {
  biotech: "https://images.unsplash.com/photo-1705871433057-4bf8911f136f?crop=entropy&cs=srgb&fm=jpg&q=85",
  healthcare: "https://images.unsplash.com/photo-1705871433057-4bf8911f136f?crop=entropy&cs=srgb&fm=jpg&q=85",
  agriculture: "https://images.unsplash.com/photo-1506180064210-cfa64ed82c11?crop=entropy&cs=srgb&fm=jpg&q=85",
  strategic: "https://images.unsplash.com/photo-1683347111036-a93bfba8b227?crop=entropy&cs=srgb&fm=jpg&q=85",
  technology: "https://images.unsplash.com/photo-1683347111036-a93bfba8b227?crop=entropy&cs=srgb&fm=jpg&q=85",
};

const categories = [
  { id: "all", label: "All Investments" },
  { id: "biotech", label: "Biotech" },
  { id: "wellness", label: "Wellness" },
  { id: "agriculture", label: "Agriculture" },
  { id: "strategic", label: "Strategic" },
  { id: "media", label: "Media & Entertainment" },
];

export default function PortfolioPage() {
  const [investments, setInvestments] = useState([]);
  const [activeFilter, setActiveFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPortfolio = async () => {
      try {
        const response = await axios.get(`${API}/portfolio`);
        setInvestments(response.data.investments);
      } catch (error) {
        console.error("Failed to fetch portfolio:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPortfolio();
  }, []);

  const filteredInvestments =
    activeFilter === "all"
      ? investments
      : investments.filter((inv) => inv.category === activeFilter);

  return (
    <main data-testid="portfolio-page" className="min-h-screen pt-20">
      {/* Hero */}
      <section className="py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-amber-400 uppercase tracking-[0.3em] text-xs mb-4"
          >
            Our Investments
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-6xl text-white mb-6"
          >
            Portfolio Companies
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-slate-400 font-light text-base md:text-lg max-w-2xl mb-4"
          >
            Strategic investments in micro-cap companies and private entities across 
            healthcare, biotechnology, and emerging technologies.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-slate-500 text-sm italic"
          >
            Investments made by Unleashed Ventures and/or by our Founder.
          </motion.p>
        </div>
      </section>

      {/* Filter */}
      <section className="px-6 md:px-12 lg:px-24 pb-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <Filter size={14} className="text-slate-500" />
            <span className="text-slate-500 text-xs uppercase tracking-wider">Filter by sector</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat.id}
                data-testid={`filter-${cat.id}`}
                onClick={() => setActiveFilter(cat.id)}
                className={`px-4 py-2 text-xs uppercase tracking-widest transition-all duration-300 ${
                  activeFilter === cat.id
                    ? "bg-amber-500 text-[#08090D] font-bold"
                    : "bg-transparent border border-slate-700 text-slate-400 hover:border-amber-400/50 hover:text-amber-400"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="px-6 md:px-12 lg:px-24 pb-24">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-24">
              <div className="w-8 h-8 loading-spinner" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredInvestments.map((company, index) => (
                <motion.div
                  key={company.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  data-testid={`portfolio-card-${company.id}`}
                  className="portfolio-card group bg-slate-900/50 border border-white/5 hover:border-amber-400/30 transition-all duration-500"
                >
                  <div className="aspect-[16/10] overflow-hidden relative bg-[#0a0c10] flex items-center justify-center">
                    {company.logo ? (
                      <img
                        src={company.logo}
                        alt={company.name}
                        className="w-full h-full object-contain p-4"
                      />
                    ) : (
                      <img
                        src={categoryImages[company.category]}
                        alt={company.name}
                        className="portfolio-card-image w-full h-full object-cover"
                      />
                    )}
                  </div>

                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className={`text-xs px-2 py-1 badge-${company.category}`}>
                        {company.category.charAt(0).toUpperCase() + company.category.slice(1)}
                      </span>
                      {company.ticker && (
                        <span className="text-amber-400 text-xs font-bold tracking-wider">
                          {company.ticker}
                        </span>
                      )}
                    </div>

                    <h3 className="text-white text-xl font-medium mb-3 group-hover:text-amber-400 transition-colors">
                      {company.name}
                    </h3>

                    <p className="text-slate-500 text-sm leading-relaxed mb-4">
                      {company.description}
                    </p>

                    <div className="flex items-center justify-between pt-4 border-t border-white/5">
                      <span
                        className={`text-xs uppercase tracking-wider ${
                          company.status === "active" 
                            ? "text-green-400" 
                            : company.status === "sold"
                            ? "text-amber-400 font-bold"
                            : "text-slate-500"
                        }`}
                      >
                        {company.status}
                      </span>
                      {company.ticker && (
                        <a
                          href={`https://finance.yahoo.com/quote/${company.ticker}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-slate-500 hover:text-amber-400 transition-colors"
                        >
                          <ExternalLink size={14} />
                        </a>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {!loading && filteredInvestments.length === 0 && (
            <div className="text-center py-24">
              <p className="text-slate-500">No investments found in this category.</p>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
